/**
 * THE PRODUCT'S OWN ICON SET.
 *
 * Inline paths rather than a dependency: two dozen glyphs are not worth one,
 * and a dependency on the first screens somebody sees would be a third-party
 * request before they have seen anything. Everything is drawn on a 24-grid
 * with a 1.7 stroke and `currentColor`, so an icon inherits whatever text it
 * sits in and there is one place to change the weight.
 */
export type IconName =
  // The flow
  | 'script'
  | 'pin'
  | 'scout'
  | 'shortlist'
  // Promises
  | 'bolt'
  | 'team'
  | 'map'
  // Production formats
  | 'feature'
  | 'series'
  | 'commercial'
  | 'musicVideo'
  | 'shortFilm'
  | 'documentary'
  | 'microContent'
  | 'other'
  // The script surface
  | 'upload'
  | 'pencil'
  | 'lock'
  // Form affordances
  | 'title'
  | 'model'
  | 'phase'
  | 'check'
  // The creation page: the promise column, the billing notes, the way back
  | 'search'
  | 'star'
  | 'info'
  | 'arrowLeft'
  | 'arrowRight'
  | 'play'
  | 'flag'
  | 'archive'
  // The Overview: the workflow's own nouns
  | 'scenes'
  | 'checklist'
  | 'nodes'
  | 'clock'
  | 'alert'
  // The shared workspace UI kit (components/ui): menus, disclosure, views
  | 'more'
  | 'chevronDown'
  | 'chevronRight'
  | 'close'
  | 'list'
  | 'grid'
  | 'filter'
  // The Team page: inviting, invitations, roles and access
  | 'userPlus'
  | 'mail'
  | 'shield'
  // The References library: what a reference is, and what can be done to it
  | 'link'
  | 'note'
  | 'image'
  | 'video'
  | 'pdf'
  | 'file'
  | 'folder'
  | 'layers'
  | 'tag'
  | 'chat'
  | 'copy'
  | 'trash'
  | 'plus'
  | 'cloudUpload'
  | 'external'
  // The Files tab: a spreadsheet and a slide deck beside the document icons above
  | 'sheet'
  | 'slides';

const PATHS: Readonly<Record<IconName, string>> = {
  script: 'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zM14 2v6h6M8 13h8M8 17h5',
  pin: 'M12 21s7-6.2 7-11a7 7 0 1 0-14 0c0 4.8 7 11 7 11z M12 10.5v.01',
  scout: 'M6 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6zM18 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6zM9 12h6M7 9l1.5-5h7L17 9',
  shortlist:
    'M4 6h10M4 12h10M4 18h10M18 5l1.6 3.3 3.4.5-2.5 2.4.6 3.4-3.1-1.6-3.1 1.6.6-3.4-2.5-2.4 3.4-.5z',
  bolt: 'M13 2 4 14h7l-1 8 9-12h-7z',
  team: 'M9 11a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM3 20c0-3 2.7-5 6-5s6 2 6 5M17 11a3 3 0 1 0-1.5-5.6M17 14c2.4.4 4 2.2 4 4.4',
  map: 'M9 4 3 6.5v13L9 17l6 2.5 6-2.5v-13L15 6.5zM9 4v13M15 6.5v13',

  feature: 'M3 8.5A1.5 1.5 0 0 1 4.5 7h8A1.5 1.5 0 0 1 14 8.5v7A1.5 1.5 0 0 1 12.5 17h-8A1.5 1.5 0 0 1 3 15.5zM14 11l5-3v8l-5-3zM6 4.5h.01M10 4.5h.01',
  series: 'M3 5h18v11H3zM8 20h8M12 16v4',
  commercial: 'M3 8h18v12H3zM3 8l2.5-4h13L21 8M8.5 4 6 8M14 4l-2.5 4',
  musicVideo: 'M9 18V6l10-2v12M9 18a2.5 2.5 0 1 1-5 0 2.5 2.5 0 0 1 5 0zM19 16a2.5 2.5 0 1 1-5 0 2.5 2.5 0 0 1 5 0z',
  shortFilm: 'M4 4h16v16H4zM10 9l5 3-5 3z',
  documentary: 'M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8zM14 3v5h5M9 13h6M9 17h4',
  upload: 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12',
  pencil: 'M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z',
  lock: 'M5.5 11h13v10h-13zM8.5 11V7.5a3.5 3.5 0 0 1 7 0V11',
  microContent: 'M8 2h8a1 1 0 0 1 1 1v18a1 1 0 0 1-1 1H8a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1zM10.5 10.5l4 2-4 2z',
  other: 'M5 12h.01M12 12h.01M19 12h.01',

  title: 'M5 6h14M9 6v12M7 18h4',
  model: 'M12 3 3 7.5 12 12l9-4.5zM3 12.5 12 17l9-4.5M3 17 12 21.5 21 17',
  phase: 'M12 7V3M12 21v-4M5.6 5.6 8.5 8.5M15.5 15.5l2.9 2.9M3 12h4M17 12h4M5.6 18.4l2.9-2.9M15.5 8.5l2.9-2.9',
  check: 'M4 12.5 9 17.5 20 6.5',

  search: 'M11 4a7 7 0 1 0 0 14 7 7 0 0 0 0-14zM16 16l4.5 4.5',
  star: 'M12 3.5l2.6 5.3 5.9.9-4.2 4.1 1 5.8-5.3-2.8-5.3 2.8 1-5.8L3.5 9.7l5.9-.9z',
  info: 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18zM12 11v5M12 7.6h.01',
  arrowLeft: 'M20 12H4M10 6l-6 6 6 6',
  arrowRight: 'M4 12h16M14 6l6 6-6 6',
  play: 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18zM10 8.5l6 3.5-6 3.5z',
  flag: 'M5 21V4M5 4h11l-2 3.5L16 11H5',
  archive: 'M3 4h18v4H3zM5 8v12h14V8M10 12h4',
  // A clapperboard: the slate and its sticks.
  scenes: 'M3 9h18v11H3zM3 9l1.5-5h15L21 9M8.5 4 7 9M13.5 4 12 9M18.5 4 17 9',
  checklist: 'M10 6h10M10 12h10M10 18h10M4 6l1.2 1.2L7.5 5M4 12l1.2 1.2 2.3-2.2M4 18l1.2 1.2 2.3-2.2',
  nodes:
    'M6 4a2 2 0 1 1 0 4 2 2 0 0 1 0-4zM18 4a2 2 0 1 1 0 4 2 2 0 0 1 0-4zM12 16a2 2 0 1 1 0 4 2 2 0 0 1 0-4zM7 7.8l4 8.4M17 7.8l-4 8.4',
  clock: 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18zM12 7.5V12l3 2',
  alert: 'M12 3.5 2.5 20h19zM12 10v4.5M12 17.4v.01',
  more: 'M5 12h.01M12 12h.01M19 12h.01',
  chevronDown: 'M6 9l6 6 6-6',
  chevronRight: 'M9 6l6 6-6 6',
  close: 'M6 6l12 12M18 6 6 18',
  list: 'M9 6h12M9 12h12M9 18h12M4 6h.01M4 12h.01M4 18h.01',
  grid: 'M4 4h7v7H4zM13 4h7v7h-7zM4 13h7v7H4zM13 13h7v7h-7z',
  filter: 'M3 5h18l-7 8.5V19l-4 2v-7.5z',
  userPlus: 'M10 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM2.5 21c0-3.9 3.4-7 7.5-7s7.5 3.1 7.5 7M19 8v6M16 11h6',
  mail: 'M3 5.5h18v13H3zM3.5 6l8.5 7 8.5-7',
  shield: 'M12 3 4.5 6v5.5c0 4.6 3.2 8.3 7.5 9.5 4.3-1.2 7.5-4.9 7.5-9.5V6z',
  link: 'M10 14a4.5 4.5 0 0 0 6.4 0l3-3a4.5 4.5 0 0 0-6.4-6.4l-1.2 1.2M14 10a4.5 4.5 0 0 0-6.4 0l-3 3a4.5 4.5 0 0 0 6.4 6.4l1.2-1.2',
  note: 'M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9zM14 3v6h6M8 13h8M8 17h6',
  image: 'M4 4h16v16H4zM4 16l4.5-4.5 3.5 3.5 3-3L20 17M15.5 8.5h.01',
  video: 'M3 6h12v12H3zM15 10l6-3.5v11L15 14z',
  pdf: 'M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9zM14 3v6h6M8 17v-4h1.5a1.25 1.25 0 0 1 0 2.5H8M13 13v4h1a2 2 0 0 0 0-4zM18 13h-1.5v4M16.5 15H18',
  file: 'M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9zM14 3v6h6',
  folder: 'M3 6.5A1.5 1.5 0 0 1 4.5 5H9l2 2.5h8.5A1.5 1.5 0 0 1 21 9v9.5a1.5 1.5 0 0 1-1.5 1.5h-15A1.5 1.5 0 0 1 3 18.5z',
  layers: 'M12 3 3 7.5 12 12l9-4.5zM3 12l9 4.5 9-4.5M3 16.5 12 21l9-4.5',
  tag: 'M3 12V4h8l10 10-8 8zM7.5 8h.01',
  chat: 'M21 12a8 8 0 0 1-11.6 7.1L4 20.5l1.4-4.6A8 8 0 1 1 21 12z',
  copy: 'M9 9h11v11H9zM5 15H4V4h11v1',
  trash: 'M4 7h16M10 11v6M14 11v6M6 7l1 13h10l1-13M9 7V4h6v3',
  plus: 'M12 5v14M5 12h14',
  cloudUpload: 'M7 18H6a4 4 0 0 1-.6-8A6 6 0 0 1 17 8.5h.5a4.5 4.5 0 0 1 .5 9H17M12 12v8M9 15l3-3 3 3',
  external: 'M14 4h6v6M20 4l-9 9M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5',
  // A page with a ruled grid: a spreadsheet.
  sheet: 'M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9zM14 3v6h6M8 12h8v6H8zM8 15h8M12 12v6',
  // A screen on a stand: a presentation.
  slides: 'M3 4h18M4 4v11h16V4M12 15v3M8.5 21l3.5-3 3.5 3M8 10.5l2.5-2 2 1.5 3-3',
};

export function Icon({
  name,
  size = 16,
  strokeWidth = 1.7,
}: {
  name: IconName;
  size?: number;
  strokeWidth?: number;
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ flexShrink: 0 }}
    >
      <path d={PATHS[name]} />
    </svg>
  );
}
